<template>
  <CDrawer
    class="c-file-viewer-page-drawer"
    placement="right"
    show
    :style="{
      border: `1px solid ${themeVars.primary5}`,
      'margin-top': '8px',
      'border-radius': '8px 0px 0px 8px',
    }"
    :width="440"
  >
    <CButton
      class="absolute top-[24px] right-[440px] z-12 rounded mr-1 h-11 w-11"
      :disabled="props.isSavingPagesMetadata"
      :style="{ background: themeVars.primary5, border: `1px solid ${themeVars.primary}` }"
      @click="onClose"
    >
      <template #icon>
        <CIcon icon="close" size="large" />
      </template>
    </CButton>

    <CDrawerContent :closable="false">
      <CSpin v-if="isLoadingInstanceMetadataTemplate" class="flex h-full" />

      <template v-if="!isLoadingInstanceMetadataTemplate" #header>
        <div class="flex flex-col gap-1">
          <div class="flex gap-2">
            <CSelect
              bordered
              class="flex-grow-1"
              :disabled="isSavingPagesMetadata"
              filterable
              hideDetails
              :menuProps="{
                style: {
                  '--n-option-height': '185px',
                  'box-shadow': '2px 2px 4px 4px rgba(0, 0, 0, 0.12)',
                },
              }"
              :options="pageSelectOptions"
              :render-option="renderPageOption"
              :to="false"
              :value="currentPage"
              @update:value="setPage"
            />
            <CButton
              :disabled="currentPageIndex === 0 || isSavingPagesMetadata"
              :height="36"
              secondary
              :width="36"
              @click="previousPage"
            >
              <CIcon icon="angle-left" size="16px" />
            </CButton>
            <CButton
              :disabled="currentPageIndex === pageCount - 1 || isSavingPagesMetadata"
              :height="36"
              secondary
              :width="36"
              @click="nextPage"
            >
              <CIcon icon="angle-right" size="16px" />
            </CButton>
          </div>
          <div class="font-size-3 color-text2">
            {{ t('canvasUI.components.fileViewer.pageMetadata.form.currentPageHelpText') }}
          </div>
        </div>
      </template>

      <div v-if="!isLoadingInstanceMetadataTemplate" class="flex flex-col gap-4 h-full">
        <div class="font-size-5">{{ t('canvasUI.components.fileViewer.pageMetadata.form.pageSettings') }}</div>

        <NForm :model="flatPagesInfoModel[currentPageIndex]">
          <NGrid :cols="1">
            <NFormItemGridItem :label="t('canvasUI.components.fileViewer.pageMetadata.form.label.name')">
              <NInput
                :key="uiKey"
                clearable
                :disabled="isSavingPagesMetadata"
                :placeholder="t('canvasUI.components.fileViewer.pageMetadata.form.placeholder.name')"
                :value="flatPagesInfoModel[currentPageIndex]?.name"
                @update:value="updateField('name', $event, flatPagesInfoModel)"
              />
            </NFormItemGridItem>

            <NFormItemGridItem :label="t('canvasUI.components.fileViewer.pageMetadata.form.label.tags')">
              <CTags
                :key="uiKey"
                :disabled="isSavingPagesMetadata"
                :value="flatPagesInfoModel[currentPageIndex]?.tags"
                @update:value="updateField('tags', $event, flatPagesInfoModel)"
              />
            </NFormItemGridItem>
          </NGrid>
        </NForm>
        <template v-if="instanceMetadataTemplate && !isInstanceTemplateEmpty">
          <div class="font-size-5">{{ t('canvasUI.components.fileViewer.pageMetadata.form.metadata') }}</div>
          <CConfigEditor
            :key="uiKey"
            ref="configEditorRef"
            ignoreRequired
            :initialValues="flatPagesMetadataModel[currentPageIndex]"
            :loading="isSavingPagesMetadata"
            :template="instanceMetadataTemplate as ConfigForm"
            @change="updateMetadata as Record<string, any>"
          />
        </template>
      </div>
      <template v-if="!isLoadingInstanceMetadataTemplate" #footer>
        <div v-if="!isLoadingInstanceMetadataTemplate" class="flex flex-col flex-1 gap-2">
          <div class="flex flex-1 justify-center gap-2">
            <div class="flex w-full">
              <CopyPopover
                :key="uiKey"
                :disabled="isSavingPagesMetadata"
                :option="copyOption"
                :pageCount="pageCount"
                @copy="copyMetadata"
              />
            </div>
            <div class="flex w-full">
              <CButton
                class="flex-1"
                :disabled="!hasUnsavedChanges"
                :loading="isSavingPagesMetadata"
                type="primary"
                @click="save"
              >
                {{ t('canvasUI.components.fileViewer.pageMetadata.form.saveChanges') }}
              </CButton>
            </div>
          </div>
          <CButton
            class="flex w-full"
            :disabled="isSavingPagesMetadata || !hasUnsavedChanges"
            quaternary
            @click="discard"
          >
            {{ t('canvasUI.components.fileViewer.pageMetadata.form.discardChanges') }}
          </CButton>
        </div>
      </template>
    </CDrawerContent>
  </CDrawer>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed, h, nextTick, reactive, ref, watch, type VNode } from 'vue'
import CDrawer from '@lib/components/CDrawer/CDrawer.vue'
import CDrawerContent from '@lib/components/CDrawerContent/CDrawerContent.vue'
import CButton from '@lib/components/CButton/CButton.vue'
import CTags from '@lib/components/CTags/CTags.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CSelect from '@lib/components/CSelect/CSelect.vue'
import CSpin from '@lib/components/CSpin/CSpin.vue'
import CConfigEditor from '@lib/components/CConfigEditor/CConfigEditor.vue'
import CopyPopover, { type CopyOption } from './CFileViewerCopyPagesMetadata.pdf.vue'
import { NForm, NInput, NGrid, NFormItemGridItem, type SelectOption } from 'naive-ui'
import type { FileMetadata, PitcherFile } from '@lib/types/file.types'
import type { MetadataTemplate } from '@openapi'
import type { ConfigForm } from '@lib/components/CConfigEditor/CConfigEditor.types'
import SelectPage from './CFileViewerPageDrawerSelectPage.vue'

import useThemeVars from '@lib/composables/useThemeVars'
import useConfirmation from '@lib/composables/useConfirmation'
import { cloneDeep, isEmpty, isEqual, every, isNull } from 'lodash-es'

interface Props {
  show?: boolean
  fileMetadata: PitcherFile['metadata']
  pagesImageUrls: string[]
  pageIndex: number
  pageCount: number
  instanceMetadataTemplate: MetadataTemplate | null
  isSavingPagesMetadata?: boolean
  isLoadingInstanceMetadataTemplate?: boolean
}

type Emits = {
  close: []
  save: [
    {
      pagesInfo: FileMetadata['pagesInfo']
      pagesMetadata: FileMetadata['pagesMetadata']
    },
  ]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()
const { t } = useI18n()
const themeVars = useThemeVars()
const confirmation = useConfirmation()
const configEditorRef = ref<null | InstanceType<typeof CConfigEditor>>(null)

const flatPagesMetadata = computed(() => flattenMetadata(props.fileMetadata.pagesMetadata ?? {}))
const flatPageInfo = computed(() => flattenMetadata(props.fileMetadata.pagesInfo ?? {}))
const flatPagesMetadataModel = reactive(cloneDeep(flatPagesMetadata.value))
const flatPagesInfoModel = reactive(cloneDeep(flatPageInfo.value))

const uiKey = ref(0)

const currentPageIndex = ref(props.pageIndex)
const currentPage = computed(() => currentPageIndex.value + 1)
const copyOption = computed<CopyOption>(() => (currentPageIndex.value === 0 ? 'all' : null))
const isInstanceTemplateEmpty = computed(() => !props.instanceMetadataTemplate?.sections?.length)
const pageSelectOptions = computed(() => {
  return Array.from({ length: props.pageCount }, (_, index) => ({
    value: index + 1,
    label: t('canvasUI.components.fileViewer.pageMetadata.form.page', { page: index + 1 }),
    imageUrl: props.pagesImageUrls[index],
  }))
})

const hasUnsavedChanges = computed(() => {
  const pagesInfoChanged = !isEqual(flatPagesInfoModel, flatPageInfo.value)
  const pagesMetadataChanged = !isEqual(flatPagesMetadataModel, flatPagesMetadata.value)

  return pagesInfoChanged || pagesMetadataChanged
})

function renderPageOption({
  node,
  option,
}: {
  node: VNode
  option: SelectOption & { imageUrl: string; index: number }
}) {
  return h(node, [
    h(SelectPage, {
      class: 'flex flex-col justify-center z-0',
      option,
    }),
  ])
}

function flattenMetadata<T extends Record<string, any>>(pagesMetadata: Record<string, T>): Record<string, T> {
  // Initialize flatMetadata with empty metadata for each page based on pageCount
  const flatMetadata: Record<string, T> = {}
  for (let i = 0; i < props.pageCount; i++) {
    flatMetadata[i.toString()] = {} as unknown as T // Cast empty object to T
  }

  // Overwrite flatMetadata with actual metadata from pagesMetadata
  Object.entries(pagesMetadata).forEach(([key, metadata]) => {
    if (key.includes('-')) {
      const [startStr, endStr] = key.split('-').map(Number)
      for (let i = startStr; i <= endStr; i++) {
        flatMetadata[i.toString()] = metadata
      }
    } else {
      flatMetadata[key] = metadata
    }
  })

  return flatMetadata
}

function updateMetadata(metadata: Record<string, any>) {
  flatPagesMetadataModel[currentPageIndex.value] = metadata
}

function updateField<T>(key: keyof T, value: T[keyof T], flatModel: Record<string, T>) {
  const currentPageIndexStr = currentPageIndex.value.toString()

  const currentData = { ...flatModel[currentPageIndexStr] }

  // Check if the value is an empty string or an empty array
  if ((typeof value === 'string' && value.trim() === '') || (Array.isArray(value) && value.length === 0)) {
    delete currentData[key] // Delete the key from the data object
  } else {
    currentData[key] = value // Otherwise, set the value
  }

  flatModel[currentPageIndexStr] = currentData
}

function copyMetadata(option: CopyOption, range?: number[]) {
  const pageIndex = currentPageIndex.value
  const currentMetadata = flatPagesMetadataModel[pageIndex]

  const clonedMetadata = cloneDeep(currentMetadata)

  switch (option) {
    case 'all':
      Object.keys(flatPagesMetadataModel).forEach((key) => {
        flatPagesMetadataModel[key] = clonedMetadata
      })
      break
    case 'range':
      if (range && range[0] <= range[1] && range[1] < props.pageCount) {
        for (let i = range[0]; i <= range[1]; i++) {
          flatPagesMetadataModel[i] = clonedMetadata
        }
      } else {
        console.error('Invalid range provided for copying metadata.')
      }
      break
    case 'unedited':
      for (let i = 0; i < props.pageCount; i++) {
        if (isEmpty(flatPagesMetadataModel[i]) || every(flatPagesMetadataModel[i], isNull)) {
          flatPagesMetadataModel[i] = clonedMetadata
        }
      }
      break
  }
}
function discard() {
  Object.assign(flatPagesMetadataModel, cloneDeep(flatPagesMetadata.value))
  Object.assign(flatPagesInfoModel, cloneDeep(flatPageInfo.value))

  uiKey.value += 1
}
function consolidateMetadata<T>(flatPagesData: Record<string, T>): Record<string, T> {
  const sortedIndices = Object.keys(flatPagesData)
    .map(Number)
    .filter((index) => !isEmpty(flatPagesData[index.toString()])) // Filter out empty objects
    .sort((a, b) => a - b)

  const finalData: Record<string, T> = {}
  let rangeStart: number | null = null
  let rangeEnd: number | null = null
  let currentData: T | null = null

  sortedIndices.forEach((pageIndex, i) => {
    const pageData = flatPagesData[pageIndex.toString()]

    // If currentData is null, we're starting a new range
    if (currentData === null) {
      rangeStart = pageIndex
      rangeEnd = pageIndex
      currentData = pageData
    } else if (isEqual(currentData, pageData)) {
      // Extend the current range if the data is equal
      rangeEnd = pageIndex
    } else {
      // We've encountered different data, so save the current range
      if (rangeStart !== null && rangeEnd !== null) {
        const key = rangeStart === rangeEnd ? `${rangeStart}` : `${rangeStart}-${rangeEnd}`
        finalData[key] = currentData
      }
      // Start a new range
      rangeStart = pageIndex
      rangeEnd = pageIndex
      currentData = pageData
    }

    // Check if we're at the last index to save the final range
    if (i === sortedIndices.length - 1 && rangeStart !== null && rangeEnd !== null) {
      const key = rangeStart === rangeEnd ? `${rangeStart}` : `${rangeStart}-${rangeEnd}`
      finalData[key] = currentData
    }
  })

  return finalData
}
function save() {
  const pagesMetadataToSave = consolidateMetadata(flatPagesMetadataModel)
  const pagesInfoToSave = consolidateMetadata(flatPagesInfoModel)
  const fileMetadataToSave = {
    pagesInfo: pagesInfoToSave,
    pagesMetadata: pagesMetadataToSave,
  }

  emit('save', fileMetadataToSave)
}

function previousPage() {
  if (currentPageIndex.value > 0) currentPageIndex.value -= 1
  uiKey.value += 1
}
function nextPage() {
  if (currentPageIndex.value < props.pageCount - 1) currentPageIndex.value += 1
  uiKey.value += 1
}
function setPage(page: number) {
  currentPageIndex.value = page - 1
  uiKey.value += 1
}

function onClose() {
  if (!hasUnsavedChanges.value) {
    emit('close')
    return
  }

  confirmation.warning({
    hasExit: true,
    title: t('canvasUI.components.fileViewer.pageMetadata.closeDialog.title'),
    content: t('canvasUI.components.fileViewer.pageMetadata.closeDialog.description'),
    confirmText: t('canvasUI.components.fileViewer.pageMetadata.closeDialog.save'),
    cancelText: t(t('canvasUI.components.fileViewer.pageMetadata.closeDialog.cancel')),
    exitText: t('canvasUI.components.fileViewer.pageMetadata.closeDialog.doNotSave'),
    onExit: () => {
      discard()
      emit('close')
    },
    onConfirm: async () => {
      await save()
      await nextTick()

      const stopWatcher = watch(
        () => props.isSavingPagesMetadata,
        (isSaving) => {
          if (!isSaving) {
            emit('close')
            stopWatcher()
          }
        },
        { immediate: true },
      )
    },
  })
}
</script>

<style lang="scss" scoped>
:deep(.n-base-select-menu .n-base-select-option) {
  padding: 4px 8px;
}

:deep(.n-base-select-menu .n-virtual-list),
:deep(.n-scrollbar),
:deep(.n-select-menu) {
  max-height: calc(100vh - 80px);
}
</style>
